import { useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import PhotoCalls from "../../../config/calls/PhotoCalls.js";
import { PhotoQueueItemStates, PhotoSessionStates } from "../../../config/PhotoConfigs.js";
import { resolveQueueItemTags } from "../../../utils/photoTagUtils.js";
import { runPhotoUploadQueue, uploadPhotoBytes } from "../../../utils/photoUploadQueue.js";
import PhotoUploadSessionContext from "./PhotoUploadSessionContext.js";

/**
 * @param {object} dtoIn
 * @returns {Promise<object>}
 */
async function createPhoto(dtoIn) {
  const response = await PhotoCalls.create(dtoIn);
  return response.data;
}

/**
 * @param {{ photoId: string }} dtoIn
 * @returns {Promise<object>}
 */
async function confirmUploaded(dtoIn) {
  const response = await PhotoCalls.uploaded(dtoIn);
  return response.data;
}

/**
 * In-memory photo upload session. Survives in-app navigation; cleared on full page reload.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
function PhotoUploadSessionProvider({ children }) {
  const [state, setState] = useState(PhotoSessionStates.IDLE);
  const [raceId, setRaceId] = useState(null);
  const [items, setItems] = useState([]);
  const [currentFilename, setCurrentFilename] = useState("");
  const [uploadedCount, setUploadedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [folderTagsByPath, setFolderTagsByPath] = useState({});
  const [sessionTags, setSessionTagsState] = useState([]);
  const abortRef = useRef(null);
  const runningRef = useRef(false);
  const itemsRef = useRef([]);
  const raceIdRef = useRef(null);
  const folderTagsRef = useRef({});
  const sessionTagsRef = useRef([]);

  itemsRef.current = items;
  raceIdRef.current = raceId;
  folderTagsRef.current = folderTagsByPath;
  sessionTagsRef.current = sessionTags;

  const patchItem = useCallback((localId, patch) => {
    setItems((prev) => prev.map((item) => (item.localId === localId ? { ...item, ...patch } : item)));
  }, []);

  const setFolderTags = useCallback((path, tags) => {
    setFolderTagsByPath((prev) => ({ ...prev, [path]: tags }));
  }, []);

  const setFileTags = useCallback(
    (localId, tags) => {
      patchItem(localId, { tags });
    },
    [patchItem],
  );

  const setSessionTags = useCallback((tags) => {
    setSessionTagsState(tags);
  }, []);

  const loadReview = useCallback((nextRaceId, nextItems, nextFolderTags = {}) => {
    if (runningRef.current) {
      return;
    }
    abortRef.current = null;
    setRaceId(nextRaceId);
    setFolderTagsByPath(nextFolderTags);
    setSessionTagsState([]);
    setItems(
      (nextItems ?? []).map((item, index) => ({
        ...item,
        localId: item.localId ?? `photo-${index}`,
        tags: item.tags ?? [],
        state: item.state ?? PhotoQueueItemStates.QUEUED,
      })),
    );
    setCurrentFilename("");
    setUploadedCount(0);
    setTotalCount(nextItems?.length ?? 0);
    setState(PhotoSessionStates.REVIEW);
  }, []);

  const runQueue = useCallback(
    async (queueItems, sessionRaceId) => {
      if (runningRef.current || !sessionRaceId || !queueItems.length) {
        return;
      }

      const controller = new AbortController();
      abortRef.current = controller;
      runningRef.current = true;
      setState(PhotoSessionStates.RUNNING);
      setUploadedCount(0);
      setTotalCount(queueItems.length);
      setCurrentFilename("");

      await runPhotoUploadQueue({
        raceId: sessionRaceId,
        items: queueItems,
        createPhoto,
        uploadBytes: uploadPhotoBytes,
        confirmUploaded,
        signal: controller.signal,
        onItemStart: (item) => {
          setCurrentFilename(item.filename);
          patchItem(item.localId, { state: PhotoQueueItemStates.UPLOADING });
        },
        onItemSuccess: (item) => {
          patchItem(item.localId, { state: PhotoQueueItemStates.UPLOADED, photoId: item.photoId });
          setUploadedCount((count) => count + 1);
        },
        onItemSkip: (item) => {
          patchItem(item.localId, { state: PhotoQueueItemStates.SKIPPED });
        },
        onItemError: (item, error) => {
          patchItem(item.localId, {
            state: PhotoQueueItemStates.FAILED,
            photoId: item.photoId,
            errorMessage: error?.message || String(error),
          });
        },
      });

      runningRef.current = false;
      setCurrentFilename("");
      if (controller.signal.aborted) {
        setState(PhotoSessionStates.CANCELLED);
        return;
      }
      setState(PhotoSessionStates.DONE);
    },
    [patchItem],
  );

  const startUpload = useCallback(async () => {
    const sessionRaceId = raceIdRef.current;
    const queued = itemsRef.current
      .filter((item) => item.state === PhotoQueueItemStates.QUEUED && !item.skipReason)
      .map((item) => ({
        ...item,
        tags: resolveQueueItemTags(item, folderTagsRef.current, sessionTagsRef.current),
      }));
    await runQueue(queued, sessionRaceId);
  }, [runQueue]);

  const cancelUpload = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const retryFailed = useCallback(async () => {
    if (runningRef.current) {
      return;
    }
    const failed = itemsRef.current.filter((item) => item.state === PhotoQueueItemStates.FAILED);
    const retryItems = [];
    for (const item of failed) {
      if (item.photoId) {
        try {
          await PhotoCalls.delete({ _id: item.photoId });
        } catch (error) {
          const status = error?.response?.status ?? error?.status;
          if (status !== 404) {
            patchItem(item.localId, {
              errorMessage: error?.message || String(error),
            });
            continue;
          }
        }
      }
      const nextItem = {
        ...item,
        state: PhotoQueueItemStates.QUEUED,
        photoId: undefined,
        errorMessage: undefined,
      };
      retryItems.push({
        ...nextItem,
        tags: resolveQueueItemTags(nextItem, folderTagsRef.current, sessionTagsRef.current),
      });
      patchItem(item.localId, { state: PhotoQueueItemStates.QUEUED, photoId: undefined, errorMessage: undefined });
    }
    await runQueue(retryItems, raceIdRef.current);
  }, [patchItem, runQueue]);

  const clearSession = useCallback(() => {
    abortRef.current?.abort();
    runningRef.current = false;
    abortRef.current = null;
    setState(PhotoSessionStates.IDLE);
    setRaceId(null);
    setItems([]);
    setFolderTagsByPath({});
    setSessionTagsState([]);
    setCurrentFilename("");
    setUploadedCount(0);
    setTotalCount(0);
  }, []);

  const value = useMemo(
    () => ({
      state,
      raceId,
      items,
      folderTagsByPath,
      sessionTags,
      currentFilename,
      uploadedCount,
      totalCount,
      loadReview,
      startUpload,
      cancelUpload,
      retryFailed,
      clearSession,
      setFolderTags,
      setFileTags,
      setSessionTags,
    }),
    [
      state,
      raceId,
      items,
      folderTagsByPath,
      sessionTags,
      currentFilename,
      uploadedCount,
      totalCount,
      loadReview,
      startUpload,
      cancelUpload,
      retryFailed,
      clearSession,
      setFolderTags,
      setFileTags,
      setSessionTags,
    ],
  );

  return <PhotoUploadSessionContext.Provider value={value}>{children}</PhotoUploadSessionContext.Provider>;
}

PhotoUploadSessionProvider.propTypes = {
  children: PropTypes.node,
};

export default PhotoUploadSessionProvider;
